import React, { useEffect, useMemo, useState } from "react";

const LEADERSHIP_PAGE_INDEX = 4;
const GRADIENT_POSITION = "bottom";
const DEFAULT_IMAGE_POSITION = "center top";
const DEFAULT_PNG_ZOOM = "120%";
const NAME_POSITION_OVERRIDES = {
  "Tarandeep Singh": "center 20%",
  "S. Panchakshari": "center 40%",
  "Vinay Pratap Singh": "center 40%",
  "Uttam Dhiran": "center 40%",
  "Lekshmi Bhargavi": "center 40%",
  "Pradap RST": "center 40%",
  "Anil Gupta": "center 40%",
  "Ashish Singh": "center 40%",
  "Md. Saif Khan": "center 40%",
  "Manish Raj": "center 40%",
  "Ankit Tyagi": "center 60%",
  "Santosh Surve": "center 60%",
};
const PNG_ZOOM_OVERRIDES = {
  "Nehal Shah": "140%",
  "Tarandeep Singh": "110%",
  "Ankur Agarwal": "110%",
  "Anil Gupta": "135%",
};

const getSections = (data = {}) =>
  data?.page?.[LEADERSHIP_PAGE_INDEX]?.sections ?? [];

const getImagePosition = (name = "") =>
  NAME_POSITION_OVERRIDES[name.trim()] ?? DEFAULT_IMAGE_POSITION;

const getPngZoom = (name = "") =>
  PNG_ZOOM_OVERRIDES[name.trim()] ?? DEFAULT_PNG_ZOOM;

const getCardStyles = (member) => {
  const trimmedName = member.name?.trim() ?? "";
  const imageUrl = member.image ?? "";
  const isPng = imageUrl.toLowerCase().endsWith(".png");

  return {
    backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0)), url("${imageUrl}")`,
    backgroundSize: isPng
      ? `100%, ${getPngZoom(trimmedName)}`
      : "100% 38%, cover",
    backgroundPosition: `${GRADIENT_POSITION}, ${getImagePosition(
      trimmedName
    )}`,
    backgroundRepeat: "no-repeat, no-repeat",
    ...(isPng ? { backgroundColor: "#e5e7eb" } : {}),
  };
};

const Teams = ({ data = {} }) => {
  const sections = useMemo(() => getSections(data), [data]);
  const fallbackDept = sections[0]?.dept ?? "";
  const [selectedDept, setSelectedDept] = useState(fallbackDept);

  useEffect(() => {
    setSelectedDept(fallbackDept);
  }, [fallbackDept]);

  const filteredDepartment =
    sections.find((section) => section.dept === selectedDept) ?? sections[0];

  if (!filteredDepartment) {
    return null;
  }

  return (
    <div className="bg-ofWhite p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Department Switcher */}
        <div className="mt-5 flex flex-wrap justify-center gap-6 border-b border-white3 pb-4">
          {sections.map((section) => {
            const isActive = selectedDept === section.dept;
            return (
              <button
                key={section.dept}
                onClick={() => setSelectedDept(section.dept)}
                className={`relative pb-2 text-base sm:text-lg font-semibold tracking-wide transition-all ${
                  isActive
                    ? "text-[#1c3d72]"
                    : "text-black/50 hover:text-[#1c3d72]/80"
                }`}
              >
                {section.dept}
                <span
                  className={`absolute left-0 right-0 -bottom-[6px] h-1 rounded-full transition ${
                    isActive ? "bg-[#1c3d72]" : "bg-transparent"
                  }`}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>

        {/* Selected Department Card */}
        <div className="space-y-12 mt-8">
          <div>
            {/* Department Title */}
            <h2 className="text-xl font-bold text-gray-700 mb-4 border-l-4 border-gray-800 pl-3">
              {filteredDepartment.dept}
            </h2>

            {/* Team Members */}
            <div className="flex flex-wrap justify-center items-start gap-8">
              {filteredDepartment.members.map((member) => (
                <div
                  key={member.id}
                  className="group flex-1 basis-[240px] max-w-[320px]"
                >
                  <div
                    className="relative w-full aspect-square border-2 border-white3 overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
                    style={getCardStyles(member)}
                  >
                    {/* Bottom Info */}
                    <div className="absolute bottom-0 w-full p-4 text-white text-center">
                      <h3 className="text-lg font-bold">{member.name}</h3>
                      <p className="text-sm opacity-80">{member.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Teams;
